import { createApiClient, getApiErrorInfo } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';

/**
 * Service for user initialization and setup
 * Handles the complete user onboarding flow after Clerk signup
 */
class UserService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.users);
  }
  
  /**
   * Get the current authenticated user's profile.
   * Uses the JWT sub claim server-side; no userId needed.
   */
  async getMyProfile(): Promise<any> {
    try {
      const response = await this.api.get('/api/users/me');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      return null;
    }
  }

  /**
   * Get all users across all tenants (System Admin only)
   */
  async getAllSystemUsers(): Promise<any[]> {
    const response = await this.api.get('/api/users');
    return response.data;
  }

  /**
   * Get all users for a tenant (Admin only)
   */
  async getAllUsers(tenantId: string): Promise<any[]> {
    try {
      const response = await this.api.get(`/api/users?tenantId=${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch users:', error);
      throw error;
    }
  }

  /**
   * Update an existing user (Admin only)
   */
  async updateUser(userId: string, tenantId: string, userData: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/tenants/${tenantId}/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  }

  /**
   * Update a user's tenant association
   */
  async updateUserTenant(userId: string, newTenantId: string): Promise<any> {
    try {
      console.log('🔄 Updating user tenant association:', { userId, newTenantId });
      const response = await this.api.patch(`/api/users/${userId}/tenant`, {
        tenantId: newTenantId
      });
      console.log('✅ User tenant updated successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to update user tenant:', error);
      throw new Error(apiError.message || 'Failed to update user tenant association');
    }
  }

  /**
   * Delete a user (Admin only)
   */
  async deleteUser(userId: string, tenantId: string): Promise<void> {
    try {
      await this.api.delete(`/api/tenants/${tenantId}/users/${userId}`);
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  }

  async inviteUser(
    tenantId: string,
    inviteData: { email: string; role?: string; firstName?: string; lastName?: string }
  ): Promise<{ flow: 'existing_user' | 'new_user_clerk' | 'existing_clerk_user'; invitationId?: string; userId?: string; message?: string }> {
    try {
      const email = inviteData.email.trim();
      if (!email) {
        throw new Error('Email is required');
      }

      const body: Record<string, string> = {
        email,
        role: inviteData.role || 'user',
        redirectUrl: `${window.location.origin}/auth/individual-signup`,
      };
      if (inviteData.firstName?.trim()) {
        body.firstName = inviteData.firstName.trim();
      }
      if (inviteData.lastName?.trim()) {
        body.lastName = inviteData.lastName.trim();
      }
      const response = await this.api.post(`/api/tenants/${tenantId}/invitations`, body);
      return response.data;
    } catch (error) {
      console.error('Failed to invite user:', error);
      throw error;
    }
  }

  /**
   * Resend an invitation to a user who has not yet accepted (authProviderId is null).
   * Calls POST /api/tenants/{tenantId}/invitations/{userId}/resend.
   */
  async resendInvite(userId: string, tenantId: string): Promise<{ message: string; invitationId: string }> {
    try {
      const redirectUrl = `${window.location.origin}/auth/individual-signup`;
      const response = await this.api.post(`/api/tenants/${tenantId}/invitations/${userId}/resend?redirectUrl=${encodeURIComponent(redirectUrl)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to resend invite:', error);
      throw error;
    }
  }

  /**
   * Initialize new user account in backend after Clerk signup
   */
  async createBackendAccount(userData: {
    authProviderId: string;
    email: string;
    firstName: string;
    lastName: string;
    selectedModules?: string[];
    billingPeriod?: string;
    alternateEmail?: string;
  }): Promise<any> {
    try {
      const email = userData.email.trim();
      if (!email) {
        throw new Error('Email is required');
      }

      console.log('🔄 Creating backend account for user:', email);
      
      const createUserDto = {
        authProviderId: userData.authProviderId,
        email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: 'user' // Default role for new signups
      };

      const response = await this.api.post('/api/users', createUserDto);
      console.log('✅ Backend account created successfully:', response.data);
      
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to create backend account:', error);
      throw new Error(apiError.message || 'Failed to create account in database');
    }
  }

  /**
   * Update user profile with complete signup data
   */
  async updateUserProfile(userData: {
    authProviderId: string;
    firstName: string;
    lastName: string;
    selectedModules: string[];
    billingPeriod: string;
  }): Promise<any> {
    try {
      console.log('🔄 Updating user profile:', userData);
      
      // Use /me to avoid a separate round-trip to resolve the internal user ID
      const userProfile = await this.getMyProfile();
      if (!userProfile) {
        throw new Error('User not found in database');
      }

      // Update user profile data
      const updateData = {
        firstName: userData.firstName,
        lastName: userData.lastName
        // Note: selectedModules and billingPeriod could be stored in a user preferences table
        // For now, we'll just update the basic profile information
      };

      const response = await this.api.patch('/api/users/me', updateData);
      
      console.log('✅ User profile updated successfully:', response.data);
      console.log('📋 Module selections and billing period:', {
        selectedModules: userData.selectedModules,
        billingPeriod: userData.billingPeriod
      });
      
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to update user profile:', error);
      throw new Error(apiError.message || 'Failed to update user profile');
    }
  }

  /**
   * Update first/last name for the current authenticated user.
   */
  async updateMyName(firstName: string, lastName: string): Promise<void> {
    await this.api.patch('/api/users/me', { firstName, lastName });
  }

  /**
   * Update license info and education level for the current authenticated user.
   */
  async updateMyProfileDetails(data: {
    stateCode?: string | null;
    licenseNumber?: string | null;
    licenseIsActive?: boolean | null;
    educationLevel?: string | null;
    major?: string | null;
    graduationDate?: string | null;
    clearGraduationDate?: boolean;
    birthDate?: string | null;
    clearBirthDate?: boolean;
  }): Promise<any> {
    const response = await this.api.patch('/api/users/me', data);
    return response.data;
  }

  /**
   * Mark the first-login welcome video as seen so it never shows again.
   */
  async markWelcomeVideoSeen(): Promise<void> {
    await this.api.patch('/api/users/me', { hasSeenWelcomeVideo: true });
  }

  /**
   * Mark the CPE onboarding wizard as finished or skipped so the app stops
   * redirecting to it. Server records the timestamp; one-way.
   */
  async markOnboardingCompleted(): Promise<void> {
    await this.api.patch('/api/users/me', { hasCompletedOnboarding: true });
  }

  /**
   * Get areas of interest for the current user.
   */
  async getInterests(): Promise<string[]> {
    const response = await this.api.get<string[]>('/api/users/me/interests');
    return response.data;
  }

  /**
   * Replace all areas of interest for the current user.
   */
  async updateInterests(interests: string[]): Promise<string[]> {
    const response = await this.api.put<string[]>('/api/users/me/interests', { interests });
    return response.data;
  }

  /**
   * Get licenses for the current user.
   */
  async getLicenses(): Promise<UserLicenseDto[]> {
    const response = await this.api.get<UserLicenseDto[]>('/api/users/me/licenses');
    return response.data;
  }

  /**
   * Create a new license for the current user.
   */
  async createLicense(dto: CreateUserLicenseDto): Promise<UserLicenseDto> {
    const response = await this.api.post<UserLicenseDto>('/api/users/me/licenses', dto);
    return response.data;
  }

  /**
   * Update an existing license for the current user.
   */
  async updateLicense(licenseId: string, dto: UpdateUserLicenseDto): Promise<UserLicenseDto> {
    const response = await this.api.patch<UserLicenseDto>(`/api/users/me/licenses/${licenseId}`, dto);
    return response.data;
  }

  /**
   * Delete a license for the current user.
   */
  async deleteLicense(licenseId: string): Promise<void> {
    await this.api.delete(`/api/users/me/licenses/${licenseId}`);
  }

  /**
   * Get education records for the current user.
   */
  async getEducation(): Promise<UserEducationDto[]> {
    const response = await this.api.get<UserEducationDto[]>('/api/users/me/education');
    return response.data;
  }

  /**
   * Create a new education record for the current user.
   */
  async createEducation(dto: CreateUserEducationDto): Promise<UserEducationDto> {
    const response = await this.api.post<UserEducationDto>('/api/users/me/education', dto);
    return response.data;
  }

  /**
   * Update an existing education record for the current user.
   */
  async updateEducation(educationId: string, dto: UpdateUserEducationDto): Promise<UserEducationDto> {
    const response = await this.api.patch<UserEducationDto>(`/api/users/me/education/${educationId}`, dto);
    return response.data;
  }

  /**
   * Delete an education record for the current user.
   */
  async deleteEducation(educationId: string): Promise<void> {
    await this.api.delete(`/api/users/me/education/${educationId}`);
  }

  /**
   * Get work history records for the current user.
   */
  async getWorkHistoryItems(): Promise<UserWorkHistoryDto[]> {
    const response = await this.api.get<UserWorkHistoryDto[]>('/api/users/me/work-history');
    return response.data;
  }

  /**
   * Create a new work history record for the current user.
   */
  async createWorkHistoryItem(dto: CreateUserWorkHistoryDto): Promise<UserWorkHistoryDto> {
    const response = await this.api.post<UserWorkHistoryDto>('/api/users/me/work-history', dto);
    return response.data;
  }

  /**
   * Update an existing work history record for the current user.
   */
  async updateWorkHistoryItem(workHistoryId: string, dto: UpdateUserWorkHistoryDto): Promise<UserWorkHistoryDto> {
    const response = await this.api.patch<UserWorkHistoryDto>(`/api/users/me/work-history/${workHistoryId}`, dto);
    return response.data;
  }

  /**
   * Delete a work history record for the current user.
   */
  async deleteWorkHistoryItem(workHistoryId: string): Promise<void> {
    await this.api.delete(`/api/users/me/work-history/${workHistoryId}`);
  }

}

export const userService = new UserService();
export default userService;

/**
 * DTO for user license response
 */
export interface UserLicenseDto {
  id: string;
  userId: string;
  stateCode: string;
  licenseNumber?: string | null;
  issueDate?: string | null;
  expirationDate?: string | null;
  isActive: boolean;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for creating a new user license
 */
export interface CreateUserLicenseDto {
  stateCode: string;
  licenseNumber?: string | null;
  issueDate?: string | null;
  expirationDate?: string | null;
  isActive?: boolean;
  isPrimary?: boolean;
}

/**
 * DTO for updating a user license
 */
export interface UpdateUserLicenseDto {
  stateCode?: string;
  licenseNumber?: string | null;
  issueDate?: string | null;
  expirationDate?: string | null;
  isActive?: boolean;
  isPrimary?: boolean;
}

/**
 * DTO for user education response
 */
export interface UserEducationDto {
  id: string;
  userId: string;
  educationLevel: string;
  institutionName?: string | null;
  major?: string | null;
  minor?: string | null;
  graduationDate?: string | null;
  gpa?: number | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for creating a new user education record
 */
export interface CreateUserEducationDto {
  educationLevel: string;
  institutionName?: string | null;
  major?: string | null;
  minor?: string | null;
  graduationDate?: string | null;
  gpa?: number | null;
  isPrimary?: boolean;
}

/**
 * DTO for updating a user education record
 */
export interface UpdateUserEducationDto {
  educationLevel?: string;
  institutionName?: string | null;
  major?: string | null;
  minor?: string | null;
  graduationDate?: string | null;
  gpa?: number | null;
  isPrimary?: boolean;
}

/**
 * DTO for user work history response
 */
export interface UserWorkHistoryDto {
  id: string;
  userId: string;
  companyName: string;
  jobTitle: string;
  industry: string;
  startDate: string;
  endDate?: string | null;
  isCurrent: boolean;
  description: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for creating a new work history record
 */
export interface CreateUserWorkHistoryDto {
  companyName: string;
  jobTitle: string;
  industry: string;
  startDate: string;
  endDate?: string | null;
  isCurrent: boolean;
  description: string;
}

/**
 * DTO for updating a work history record
 */
export interface UpdateUserWorkHistoryDto {
  companyName?: string;
  jobTitle?: string;
  industry?: string;
  startDate?: string | null;
  endDate?: string | null;
  isCurrent?: boolean;
  description?: string;
}





